import { Suspense, lazy } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider } from "./auth/AuthContext";
import { LocationProvider } from "./locations/LocationContext";
import { OrgProvider } from "./locations/OrgContext";
import { PermissionsProvider } from "./auth/PermissionsContext";
import { RequireAuth } from "./auth/RequireAuth";
import { ToastProvider } from "./components/Toast";
import { PageGuard } from "./components/PageGuard";
import { LoadingBlock } from "./components/Spinner";
import { usePageTracking } from "./analytics";
import { useTheme } from "./theme/useTheme";
import { Layout } from "./components/Layout";

// EAGER — auth/public pages (no chunk round-trip on the unauthenticated entry path) plus the
// highest-traffic landing + Generate flows, so they paint instantly without a Suspense fallback.
import { LoginPage } from "./pages/LoginPage";
import { SetPasswordPage } from "./pages/SetPasswordPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { HomePage } from "./pages/HomePage";
import { GeneratePage } from "./pages/GeneratePage";
import { GenerateFlowPage } from "./pages/GenerateFlowPage";

// LAZY — every other (lower-traffic or heavy) leaf page is route-split into its own chunk so the
// entry bundle stays small. These pages export NAMED components, so each import() maps the named
// export onto `default` for React.lazy. Splitting ConfigEditorPage here also pulls @dnd-kit (and
// the rest of the editor panels) out of the eager entry for free.
const FormatsPage = lazy(() => import("./pages/FormatsPage").then((m) => ({ default: m.FormatsPage })));
const TemplateBrowserPage = lazy(() =>
  import("./pages/TemplateBrowserPage").then((m) => ({ default: m.TemplateBrowserPage })),
);
const TemplateDetailPage = lazy(() =>
  import("./pages/TemplateDetailPage").then((m) => ({ default: m.TemplateDetailPage })),
);
const ConfigListPage = lazy(() =>
  import("./pages/ConfigListPage").then((m) => ({ default: m.ConfigListPage })),
);
const ConfigEditorPage = lazy(() =>
  import("./pages/ConfigEditorPage").then((m) => ({ default: m.ConfigEditorPage })),
);
const CodeBooksPage = lazy(() =>
  import("./pages/CodeBooksPage").then((m) => ({ default: m.CodeBooksPage })),
);
const CodeBookEditorPage = lazy(() =>
  import("./pages/CodeBookEditorPage").then((m) => ({ default: m.CodeBookEditorPage })),
);
const CreateTemplatePage = lazy(() =>
  import("./pages/CreateTemplatePage").then((m) => ({ default: m.CreateTemplatePage })),
);
const MergePage = lazy(() => import("./pages/MergePage").then((m) => ({ default: m.MergePage })));
const ProfilePage = lazy(() =>
  import("./pages/ProfilePage").then((m) => ({ default: m.ProfilePage })),
);
const BatchPage = lazy(() => import("./pages/BatchPage").then((m) => ({ default: m.BatchPage })));
const GenerationJobsPage = lazy(() =>
  import("./pages/GenerationJobsPage").then((m) => ({ default: m.GenerationJobsPage })),
);
const GenerationJobDetailPage = lazy(() =>
  import("./pages/GenerationJobDetailPage").then((m) => ({ default: m.GenerationJobDetailPage })),
);
const SpecRegistryPage = lazy(() =>
  import("./pages/SpecRegistryPage").then((m) => ({ default: m.SpecRegistryPage })),
);
const AdminDashboardPage = lazy(() =>
  import("./pages/AdminDashboardPage").then((m) => ({ default: m.AdminDashboardPage })),
);
const GettingStartedPage = lazy(() =>
  import("./pages/GettingStartedPage").then((m) => ({ default: m.GettingStartedPage })),
);
const CreateDeliverablePage = lazy(() =>
  import("./pages/CreateDeliverablePage").then((m) => ({ default: m.CreateDeliverablePage })),
);
const GetStartedMigratePage = lazy(() =>
  import("./pages/GetStartedMigratePage").then((m) => ({ default: m.GetStartedMigratePage })),
);
const OnboardingPage = lazy(() =>
  import("./pages/OnboardingPage").then((m) => ({ default: m.OnboardingPage })),
);
// Phase 5/6/7 pages
const VersionHistoryPage = lazy(() =>
  import("./pages/VersionHistoryPage").then((m) => ({ default: m.VersionHistoryPage })),
);
const OrgAdminPage = lazy(() =>
  import("./pages/OrgAdminPage").then((m) => ({ default: m.OrgAdminPage })),
);
const RejectionsPage = lazy(() =>
  import("./pages/RejectionsPage").then((m) => ({ default: m.RejectionsPage })),
);
const AiUsagePage = lazy(() =>
  import("./pages/AiUsagePage").then((m) => ({ default: m.AiUsagePage })),
);
const GenAnalyticsPage = lazy(() =>
  import("./pages/GenAnalyticsPage").then((m) => ({ default: m.GenAnalyticsPage })),
);
const MarketplacePage = lazy(() =>
  import("./pages/MarketplacePage").then((m) => ({ default: m.MarketplacePage })),
);
const PageAccessAdminPage = lazy(() =>
  import("./pages/PageAccessAdminPage").then((m) => ({ default: m.PageAccessAdminPage })),
);
const AccountsAdminPage = lazy(() =>
  import("./pages/AccountsAdminPage").then((m) => ({ default: m.AccountsAdminPage })),
);
const AllOrgsPage = lazy(() =>
  import("./pages/AllOrgsPage").then((m) => ({ default: m.AllOrgsPage })),
);
const MembersAdminPage = lazy(() =>
  import("./pages/MembersAdminPage").then((m) => ({ default: m.MembersAdminPage })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30 * 1000,
    },
  },
});

/** Fires a `page_view` analytics event on every route change. Renders nothing.
 *  Must live inside <BrowserRouter> (it uses useLocation). No-op until VITE_MIXPANEL_TOKEN is set. */
function RouteTracker() {
  usePageTracking();
  return null;
}

/** Applies the resolved theme (the `dark` class on <html>) on app mount and keeps it in sync
 *  app-wide — not just when the user opens the theme switcher in the menu. The pre-paint script in
 *  index.html prevents the first-paint flash; this keeps React's view of the theme authoritative
 *  for the whole session. */
function ThemeSync() {
  useTheme();
  return null;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeSync />
      <BrowserRouter>
        <RouteTracker />
        <ToastProvider>
          <AuthProvider>
            <Suspense fallback={<LoadingBlock />}>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/set-password" element={<SetPasswordPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />

                <Route
                  element={
                    <RequireAuth>
                      <OrgProvider>
                        <LocationProvider>
                          <PermissionsProvider>
                            <Layout />
                          </PermissionsProvider>
                        </LocationProvider>
                      </OrgProvider>
                    </RequireAuth>
                  }
                >
                  <Route path="/" element={<HomePage />} />
                  <Route path="/welcome" element={<OnboardingPage />} />
                  <Route path="/new" element={<GenerateFlowPage />} />
                  <Route path="/new/classic" element={<CreateDeliverablePage />} />
                  <Route path="/setup" element={<GetStartedMigratePage />} />
                  <Route path="/formats" element={<FormatsPage />} />
                  <Route path="/templates" element={<TemplateBrowserPage />} />
                  <Route path="/templates/:classOrName" element={<TemplateDetailPage />} />
                  <Route path="/templates/new" element={<CreateTemplatePage />} />
                  <Route path="/configs" element={<ConfigListPage />} />
                  <Route path="/configs/:id" element={<ConfigEditorPage />} />
                  <Route path="/configs/:id/history" element={<VersionHistoryPage />} />
                  <Route path="/code-books" element={<CodeBooksPage />} />
                  <Route path="/code-books/:id" element={<CodeBookEditorPage />} />
                  <Route path="/merge" element={<MergePage />} />
                  <Route path="/generate" element={<GeneratePage />} />
                  <Route path="/batch" element={<PageGuard slug="batch-generation"><BatchPage /></PageGuard>} />
                  <Route path="/generate/jobs" element={<GenerationJobsPage />} />
                  <Route path="/generate/jobs/:id" element={<GenerationJobDetailPage />} />
                  <Route path="/getting-started" element={<GettingStartedPage />} />
                  <Route path="/specs" element={<PageGuard slug="spec-registry"><SpecRegistryPage /></PageGuard>} />
                  <Route path="/admin" element={<AdminDashboardPage />} />
                  <Route path="/admin/accounts" element={<AccountsAdminPage />} />
                  <Route path="/admin/orgs" element={<AllOrgsPage />} />
                  <Route path="/admin/members" element={<MembersAdminPage />} />
                  <Route path="/admin/page-access" element={<PageAccessAdminPage />} />
                  <Route path="/admin/ai-usage" element={<PageGuard slug="analytics"><AiUsagePage /></PageGuard>} />
                  <Route path="/analytics" element={<PageGuard slug="analytics"><GenAnalyticsPage /></PageGuard>} />
                  <Route path="/org" element={<OrgAdminPage />} />
                  <Route path="/rejections" element={<PageGuard slug="rejection-fix"><RejectionsPage /></PageGuard>} />
                  <Route path="/marketplace" element={<PageGuard slug="marketplace"><MarketplacePage /></PageGuard>} />
                  <Route path="/profile" element={<ProfilePage />} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </AuthProvider>
        </ToastProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
